/**
 * Loads the tenant's concept playbook library for Play Entry: every ENABLED
 * option of every stat on both sides, with season-to-date usage counts.
 *
 * One fetch per mount (`GET /playbook/v1/library`). The operator flips the
 * O/D/ST toggle constantly during a game; the library is small (a few hundred
 * rows) so it is filtered client-side per card rather than refetched.
 *
 * Failure is non-fatal by design: the built-in fields still work and the
 * platform stock lists stand in, so Play Entry is never blocked by an
 * unreachable playbook. `isUnavailable` lets the form say so quietly.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import type { StatCategory, StatOption, StatSide } from '../../lib/api-types';
import { STOCK_LIBRARY_FALLBACK } from '../playbook/stock-fallback';

export interface StatOptionLibrary {
  /** Enabled options for one stat + side, in the coordinator's order. */
  optionsFor: (category: StatCategory, side: StatSide) => readonly StatOption[];
  isLoading: boolean;
  isUnavailable: boolean;
  /** Refetch after a Playbook edit from a card's Config panel. */
  refresh: () => void;
  /** Bump usage locally after a play is logged, so "Top N" follows the game. */
  recordUse: (category: StatCategory, side: StatSide, value: string) => void;
}

function key(category: string, side: string): string {
  return `${category}:${side}`;
}

function group(items: readonly StatOption[]): Map<string, StatOption[]> {
  const map = new Map<string, StatOption[]>();
  for (const item of items) {
    if (!item.is_enabled) continue;
    const k = key(item.category, item.side);
    const bucket = map.get(k) ?? [];
    bucket.push(item);
    map.set(k, bucket);
  }
  for (const bucket of map.values()) bucket.sort((a, b) => a.sort_order - b.sort_order);
  return map;
}

/**
 * A (stat, side) the tenant holds NO rows for at all — not even disabled
 * ones — keeps the platform stock list rather than an empty card, so a tenant
 * onboarded before a catalog migration can still record a play. A stat whose
 * rows are all disabled stays empty: the coordinator chose that.
 */
function withStockFallback(
  items: readonly StatOption[],
  grouped: Map<string, StatOption[]>
): Map<string, StatOption[]> {
  const present = new Set(items.map((item) => key(item.category, item.side)));
  for (const [k, bucket] of group(STOCK_LIBRARY_FALLBACK)) {
    if (!present.has(k)) grouped.set(k, bucket);
  }
  return grouped;
}

const EMPTY: readonly StatOption[] = [];

export function useStatOptionLibrary(): StatOptionLibrary {
  const apiClient = useApiClient();
  const [groups, setGroups] = useState<Map<string, StatOption[]>>(() =>
    group(STOCK_LIBRARY_FALLBACK)
  );
  const [isLoading, setIsLoading] = useState(true);
  const [isUnavailable, setIsUnavailable] = useState(false);
  const [generation, setGeneration] = useState(0);
  const refresh = useCallback(() => setGeneration((current) => current + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    apiClient
      .getPlaybookLibrary(controller.signal)
      .then((library) => {
        if (controller.signal.aborted) return;
        setGroups(withStockFallback(library.items, group(library.items)));
        setIsUnavailable(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setGroups(group(STOCK_LIBRARY_FALLBACK));
        setIsUnavailable(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });
    return () => controller.abort();
  }, [apiClient, generation]);

  const optionsFor = useCallback(
    (category: StatCategory, side: StatSide): readonly StatOption[] =>
      groups.get(key(category, side)) ?? EMPTY,
    [groups]
  );

  const recordUse = useCallback((category: StatCategory, side: StatSide, value: string) => {
    setGroups((current) => {
      const k = key(category, side);
      const bucket = current.get(k);
      if (bucket === undefined) return current;
      const index = bucket.findIndex((o) => o.value.toLowerCase() === value.toLowerCase());
      if (index === -1) return current;
      const next = new Map(current);
      const updated = [...bucket];
      updated[index] = { ...updated[index]!, usage_count: updated[index]!.usage_count + 1 };
      next.set(k, updated);
      return next;
    });
  }, []);

  return useMemo(
    () => ({ optionsFor, isLoading, isUnavailable, refresh, recordUse }),
    [optionsFor, isLoading, isUnavailable, refresh, recordUse]
  );
}
